/**
 * בחירת שורת עמלה מ-lds_suppliers לפי היררכיית specificity + תאריכים.
 * לא משמש ל-Dynamic Packages — רק נקרא מתוך calculateLDSDealFare (LDS רגיל).
 */

/** @param {unknown} v */
export function normalizeLdsKey(v) {
  return String(v == null ? "" : v).trim();
}

/** @param {unknown} dest */
export function ldsDestinationIsAll(dest) {
  return normalizeLdsKey(dest).toUpperCase() === "ALL";
}

/**
 * פירוק שדה יעד בשורת lds_suppliers לרשימת קודים.
 * תומך ברשימה מופרדת בפסיקים (למשל SHA,BJS,XIN); גם ; כמפריד תואם לנתונים קיימים.
 * @param {unknown} dest
 * @returns {string[]}
 */
export function parseLdsDestinationList(dest) {
  const raw = normalizeLdsKey(dest);
  if (!raw) return [];
  if (raw.toUpperCase() === "ALL") return ["ALL"];
  return raw
    .split(/[,;]+/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

/** @param {unknown} cat */
export function ldsSubCategoryIsAll(cat) {
  const c = normalizeLdsKey(cat);
  return c === "" || c.toUpperCase() === "ALL";
}

/**
 * @param {unknown} supplierDest
 * @param {unknown} dealDest
 */
export function ldsDealDestinationMatchesRow(supplierDest, dealDest) {
  if (ldsDestinationIsAll(supplierDest)) return true;
  const tokens = parseLdsDestinationList(supplierDest);
  const b = normalizeLdsKey(dealDest);
  if (tokens.length === 0 || !b) return false;
  const bLower = b.toLowerCase();
  return tokens.some((token) => token.toLowerCase() === bLower);
}

/** @param {unknown} code @param {unknown} id */
export function ldsSupplierCodeMatches(code, id) {
  return normalizeLdsKey(code) === normalizeLdsKey(id);
}

/**
 * OneWay / RoundTrip / Flights from the GDS search all match lds_suppliers.lds_type = "Flights".
 * Package and organized-tour types stay as-is. Empty dealType infers from hotel/tour.
 */
export function normalizeDealTypeForLdsSupplierRow(
  dealType,
  { hotel = null, tour = null } = {}
) {
  const raw = normalizeLdsKey(dealType);
  if (!raw) {
    if (hotel && !tour) return "FlightsHotel";
    if (tour && !hotel) return "OrganizedTour";
    return "";
  }
  const compact = raw.toLowerCase().replace(/[\s_-]/g, "");
  if (
    compact === "flights" ||
    compact === "oneway" ||
    compact === "roundtrip" ||
    compact === "flight"
  ) {
    return "Flights";
  }
  return raw;
}

/** Arrival city/airport of the first outbound segment — used when the deal has no hotel/tour. */
export function extractLdsFlightDealDestination(dealLike) {
  if (!dealLike || typeof dealLike !== "object") return null;
  const lf = dealLike.linkedFlights ?? dealLike.linkedflights ?? null;
  const flightsRaw = lf?.flight ?? lf ?? null;
  const flights = Array.isArray(flightsRaw)
    ? flightsRaw
    : flightsRaw
      ? [flightsRaw]
      : [];
  const first = flights[0];
  if (!first || typeof first !== "object") return null;
  const legsRaw = first.leg ?? first.legs ?? null;
  const legs = Array.isArray(legsRaw) ? legsRaw : legsRaw ? [legsRaw] : [];
  const last = legs[legs.length - 1];
  if (!last || typeof last !== "object") return null;
  const code =
    last.arrAirport?.code ||
    last.arrAirport?.Code ||
    last.arrCity ||
    last.arrAirport?.city ||
    last.destination ||
    "";
  const trimmed = String(code || "").trim();
  return trimmed || null;
}

/** @param {unknown} rowType @param {unknown} dealType */
export function ldsTypeMatches(rowType, dealType) {
  const a = normalizeLdsKey(rowType);
  const b = normalizeLdsKey(dealType);
  return a.length > 0 && b.length > 0 && a.toLowerCase() === b.toLowerCase();
}

/** @param {Record<string, unknown>} row */
export function getRowSubCategory(row) {
  if (!row || typeof row !== "object") return "";
  return row.sub_category ?? row.category ?? "";
}

/**
 * תת-קטגוריה בשורה: ALL/ריק = פתוח לכל דיל.
 * ספציפי: מתאים רק אם לדיל יש אותה תת-קטגוריה (מנורמל).
 * @param {Record<string, unknown>} row
 * @param {unknown} dealSubCategory
 */
export function ldsSubCategoryMatchesRow(row, dealSubCategory) {
  const rowCat = normalizeLdsKey(getRowSubCategory(row));
  const dealCat = normalizeLdsKey(dealSubCategory);
  if (ldsSubCategoryIsAll(rowCat)) return true;
  if (!dealCat) return false;
  return rowCat.toLowerCase() === dealCat.toLowerCase();
}

/** @param {unknown} v */
function rowHasNonEmptyDatePart(v) {
  if (v == null) return false;
  const s = String(v).trim();
  return s.length > 0;
}

/** @param {Record<string, unknown>} row */
export function rowHasDateBounds(row) {
  if (!row || typeof row !== "object") return false;
  return (
    rowHasNonEmptyDatePart(row.valid_from ?? row.validFrom) ||
    rowHasNonEmptyDatePart(row.valid_to ?? row.validTo)
  );
}

/**
 * @param {string|null|undefined} s
 * @returns {string|null} YYYY-MM-DD
 */
export function coerceToYmd(s) {
  if (s == null) return null;
  const str = String(s).trim();
  if (!str) return null;
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  const d = new Date(str);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${da}`;
}

/**
 * טווח כולל — valid_from ו-valid_to כוללים (השוואת מחרוזות YYYY-MM-DD).
 * @param {Record<string, unknown>} row
 * @param {string|null} dealYmd
 */
export function dealYmdWithinRowBounds(row, dealYmd) {
  if (!rowHasDateBounds(row)) return true;
  if (!dealYmd) return false;
  const vfRaw = row.valid_from ?? row.validFrom;
  const vtRaw = row.valid_to ?? row.validTo;
  const vf = rowHasNonEmptyDatePart(vfRaw) ? coerceToYmd(vfRaw) : null;
  const vt = rowHasNonEmptyDatePart(vtRaw) ? coerceToYmd(vtRaw) : null;
  if (vf && dealYmd < vf) return false;
  if (vt && dealYmd > vt) return false;
  return true;
}

/**
 * @param {Record<string, unknown>} dealLike — אובייקט דיל או ארגומנטים ל-calculateLDSDealFare
 */
export function extractLdsDealReferenceDate(dealLike) {
  if (!dealLike || typeof dealLike !== "object") return null;
  const o = dealLike;
  const tryList = [
    o.dealReferenceDate,
    o.searchDate,
    o.departureDate,
    o.departure_date,
    o.checkIn,
    o.check_in,
    o.hotel && typeof o.hotel === "object" ? o.hotel.checkIn : null,
    o.hotel && typeof o.hotel === "object" ? o.hotel.check_in : null,
    o.tour && typeof o.tour === "object" ? o.tour.startDate : null,
    o.tour && typeof o.tour === "object" ? o.tour.departureDate : null,
  ];
  for (let i = 0; i < tryList.length; i++) {
    const y = coerceToYmd(tryList[i]);
    if (y) return y;
  }
  const lf = o.linkedFlights;
  if (lf) {
    const seg =
      (Array.isArray(lf) && lf[0]) ||
      lf.flight ||
      lf.outbound ||
      lf.outboundFlight ||
      null;
    if (seg && typeof seg === "object") {
      const y =
        coerceToYmd(seg.depDate) ||
        coerceToYmd(seg.departureDate) ||
        coerceToYmd(seg.dep_date) ||
        (seg.departure && coerceToYmd(seg.departure.date));
      if (y) return y;
    }
  }
  return null;
}

/**
 * מפתח דירוג לקסיקוגרפי: (יעד ספציפי, תת-קטגוריה ספציפית, טווח תאריכים פעיל והדיל בפנים)
 * @param {Record<string, unknown>} row
 * @param {string|null} dealYmd
 * @returns {{ key: [number, number, number], disqualified: boolean, disqualifyReason: string|null }}
 */
export function computeLdsRuleSortKey(row, dealYmd) {
  const specD = !ldsDestinationIsAll(row.destination);
  const specS = !ldsSubCategoryIsAll(getRowSubCategory(row));
  const hasB = rowHasDateBounds(row);
  const inR = hasB && dealYmd && dealYmdWithinRowBounds(row, dealYmd);

  if (hasB && !dealYmd) {
    return {
      key: [0, 0, 0],
      disqualified: true,
      disqualifyReason: "לשורה יש valid_from/valid_to אך לא נמצא תאריך ייחוס לדיל",
    };
  }
  if (hasB && dealYmd && !inR) {
    return {
      key: [0, 0, 0],
      disqualified: true,
      disqualifyReason: "תאריך הדיל מחוץ לטווח valid_from–valid_to",
    };
  }

  const dateActive = hasB && !!dealYmd && inR;
  return {
    key: [specD ? 1 : 0, specS ? 1 : 0, dateActive ? 1 : 0],
    disqualified: false,
    disqualifyReason: null,
  };
}

function compareKeys(a, b) {
  for (let i = 0; i < 3; i++) {
    if (a[i] !== b[i]) return a[i] - b[i];
  }
  return 0;
}

/**
 * @param {object} params
 * @param {Record<string, unknown>[]} params.rows — ldsSuppliers
 * @param {unknown} params.supplierId
 * @param {unknown} params.dealTypeForLdsSupplierRow
 * @param {unknown} params.dealDestination
 * @param {unknown} params.dealSubCategory
 * @param {string|null} params.dealYmd
 * @param {boolean} [params.debug]
 */
export function selectLdsSupplierCommissionRow({
  rows,
  supplierId,
  dealTypeForLdsSupplierRow,
  dealDestination,
  dealSubCategory,
  dealYmd,
  debug = false,
}) {
  const list = Array.isArray(rows) ? rows : [];
  /** @type {{ row: Record<string, unknown>, index: number, status: string, reasons: string[], sortKey: [number, number, number]|null, rowLabel: string }[]} */
  const evaluations = [];

  /** @type {{ row: Record<string, unknown>, index: number, key: [number, number, number] }[]} */
  const eligible = [];

  for (let i = 0; i < list.length; i++) {
    const row = list[i];
    if (!row || typeof row !== "object") continue;

    const reasons = [];
    const rowLabel = `${normalizeLdsKey(row.supplier_code)}|${normalizeLdsKey(row.destination)}|${normalizeLdsKey(getRowSubCategory(row))}|${normalizeLdsKey(row.lds_type)}`;

    if (!ldsSupplierCodeMatches(row.supplier_code, supplierId)) {
      reasons.push("supplier_code לא תואם");
      evaluations.push({
        row,
        index: i,
        status: "skipped",
        reasons,
        sortKey: null,
        rowLabel,
      });
      continue;
    }
    if (!ldsTypeMatches(row.lds_type, dealTypeForLdsSupplierRow)) {
      reasons.push("lds_type לא תואם (אחרי נרמול רישיות)");
      evaluations.push({
        row,
        index: i,
        status: "skipped",
        reasons,
        sortKey: null,
        rowLabel,
      });
      continue;
    }
    if (!ldsDealDestinationMatchesRow(row.destination, dealDestination)) {
      reasons.push("destination לא תואם");
      evaluations.push({
        row,
        index: i,
        status: "skipped",
        reasons,
        sortKey: null,
        rowLabel,
      });
      continue;
    }
    if (!ldsSubCategoryMatchesRow(row, dealSubCategory)) {
      reasons.push(
        "תת-קטגוריה בשורה ספציפית אך לדיל אין אותה קטגוריה / או אי-התאמה"
      );
      evaluations.push({
        row,
        index: i,
        status: "skipped",
        reasons,
        sortKey: null,
        rowLabel,
      });
      continue;
    }

    const sk = computeLdsRuleSortKey(row, dealYmd);
    if (sk.disqualified) {
      reasons.push(sk.disqualifyReason || "נפסל");
      evaluations.push({
        row,
        index: i,
        status: "skipped",
        reasons,
        sortKey: sk.key,
        rowLabel,
      });
      continue;
    }

    evaluations.push({
      row,
      index: i,
      status: "eligible",
      reasons: [
        `מפתח עדיפות [יעד_ספציפי, תתקט_ספציפית, טווח_תאריכים_פעיל]=[${sk.key.join(",")}]`,
      ],
      sortKey: sk.key,
      rowLabel,
    });
    eligible.push({ row, index: i, key: sk.key });
  }

  if (eligible.length === 0) {
    const out = { selected: null, evaluations, debug };
    if (debug) {
      out.debugPayload = {
        dealContext: {
          supplierId,
          dealTypeForLdsSupplierRow,
          dealDestination,
          dealSubCategory,
          dealYmd,
        },
        evaluations,
        winner: null,
        winnerExplanation: "אין שורות מתאימות אחרי מסננים + טווח תאריכים",
      };
    }
    return out;
  }

  eligible.sort((a, b) => {
    const c = compareKeys(b.key, a.key);
    if (c !== 0) return c;
    return a.index - b.index;
  });

  const winner = eligible[0];
  const explanation = `נבחרה שורה #${winner.index} עם מפתח עדיפות [${winner.key.join(",")}] (לקסיקוגרפי גבוה = ספציפי יותר; שוויון — סדר מקורי בטבלה)`;

  const out = { selected: winner.row, evaluations, debug };
  if (debug) {
    out.debugPayload = {
      dealContext: {
        supplierId,
        dealTypeForLdsSupplierRow,
        dealDestination,
        dealSubCategory,
        dealYmd,
      },
      evaluations,
      winner: {
        index: winner.index,
        key: winner.key,
        supplier_code: winner.row.supplier_code,
        destination: winner.row.destination,
        sub_category: getRowSubCategory(winner.row),
        lds_type: winner.row.lds_type,
        valid_from: winner.row.valid_from ?? winner.row.validFrom,
        valid_to: winner.row.valid_to ?? winner.row.validTo,
      },
      winnerExplanation: explanation,
      fallbackPath:
        eligible.length > 1
          ? `מועמדים: ${eligible.length}; הראשון אחרי מיון: #${winner.index}; חלופות פחות ספציפיות: ${eligible
              .slice(1, 4)
              .map((e) => `#${e.index}[${e.key.join(",")}]`)
              .join(", ")}`
          : "מועמד יחיד",
    };
  }
  return out;
}
